import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { g, p, eur } from "@/lib/api";
import { usePlan } from "@/context/PlanContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Check, Sparkles, ExternalLink, Loader2, CreditCard, Ticket } from "lucide-react";

export default function Abbonamento() {
  const { plan, refreshPlan } = usePlan();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const sessionId = params.get("session_id");
  const [checking, setChecking] = useState(!!sessionId);
  const [loading, setLoading] = useState(null);
  const [cycle, setCycle] = useState("year");
  const [voucher, setVoucher] = useState("");

  useEffect(() => {
    if (!sessionId) return;
    let tries = 0;
    const t = setInterval(async () => {
      tries += 1;
      try {
        const r = await g(`/payments/status/${sessionId}`);
        if (r.payment_status === "paid") {
          clearInterval(t);
          setChecking(false);
          await refreshPlan();
          toast.success("Abbonamento attivo, grazie!");
          nav("/abbonamento", { replace: true });
        } else if (["failed", "expired"].includes(r.payment_status) || tries > 20) {
          clearInterval(t);
          setChecking(false);
          toast.error("Pagamento non completato");
        }
      } catch {
        clearInterval(t);
        setChecking(false);
      }
    }, 2000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  if (!plan) return <p className="text-sm text-slate-500">Caricamento…</p>;

  const buy = async (lookup_key) => {
    setLoading(lookup_key);
    try {
      const r = await p("/billing/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = r.checkout_url;
    } catch {
      toast.error("Impossibile aprire il pagamento");
      setLoading(null);
    }
  };

  const portal = async () => {
    try {
      const r = await p("/billing/portal", { origin_url: window.location.origin });
      window.location.href = r.portal_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nessun abbonamento da gestire");
    }
  };

  const resLimit = plan.limits.max_reservations_month;
  const userLimit = plan.limits.max_users;

  return (
    <div className="fade-up space-y-6" data-testid="abbonamento-page">
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-500">Pannello titolare</p>
        <h1 className="mt-1 text-3xl font-bold tracking-tight sm:text-4xl">Abbonamento</h1>
      </div>

      {checking && (
        <Card className="flex items-center gap-3 p-5" data-testid="payment-checking">
          <Loader2 className="h-4 w-4 animate-spin" />
          <p className="text-sm">Confermiamo il pagamento con Stripe…</p>
        </Card>
      )}

      <Card className="p-6" data-testid="current-plan-card">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Piano attivo</p>
            <p className="font-head mt-1 text-2xl font-bold" data-testid="current-plan-name">
              {plan.plan_name} <span className="font-num text-base font-medium text-slate-500">{eur(plan.price)}/mese</span>
            </p>
            {plan.current_period_end && (
              <p className="mt-1 text-xs text-slate-500">
                {plan.cancel_at_period_end ? "Si chiude il " : "Rinnovo il "}
                {new Date(plan.current_period_end).toLocaleDateString("it-IT")}
              </p>
            )}
          </div>
          {plan.plan !== "free" && (
            <Button variant="outline" onClick={portal} data-testid="manage-subscription-btn">
              <CreditCard className="mr-1.5 h-4 w-4" /> Gestisci abbonamento
              <ExternalLink className="ml-1.5 h-3.5 w-3.5" />
            </Button>
          )}
        </div>

        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div data-testid="usage-reservations">
            <p className="text-xs font-semibold text-slate-600">
              Prenotazioni questo mese: {plan.usage.reservations_month}
              {resLimit ? ` / ${resLimit}` : " (illimitate)"}
            </p>
            <div className="mt-1.5 h-2 rounded-full bg-slate-100">
              <div
                className={`h-full rounded-full ${resLimit && plan.usage.reservations_month >= resLimit ? "bg-rose-500" : "bg-emerald-500"}`}
                style={{ width: resLimit ? `${Math.min(100, (plan.usage.reservations_month / resLimit) * 100)}%` : "100%" }}
              />
            </div>
          </div>
          <div data-testid="usage-users">
            <p className="text-xs font-semibold text-slate-600">
              Utenti attivi: {plan.usage.users}
              {userLimit ? ` / ${userLimit}` : " (illimitati)"}
            </p>
            <div className="mt-1.5 h-2 rounded-full bg-slate-100">
              <div
                className={`h-full rounded-full ${userLimit && plan.usage.users >= userLimit ? "bg-rose-500" : "bg-emerald-500"}`}
                style={{ width: userLimit ? `${Math.min(100, (plan.usage.users / userLimit) * 100)}%` : "100%" }}
              />
            </div>
          </div>
        </div>
      </Card>

      <div className="mb-4 inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white p-1" data-testid="billing-cycle-switch">
        {[["month", "Mensile"], ["year", "Annuale"]].map(([c, l]) => (
          <button
            key={c}
            data-testid={`cycle-${c}`}
            onClick={() => setCycle(c)}
            className={`rounded-full px-4 py-2 text-xs font-bold transition-colors ${
              cycle === c ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-800"
            }`}
          >
            {l}
          </button>
        ))}
        <span className="ml-1 mr-2 text-[10px] font-bold uppercase tracking-wider text-emerald-600">
          {plan.months_free_yearly || 2} mesi gratis
        </span>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {plan.plans.map((x) => {
          const current = plan.plan === x.id;
          const yearly = cycle === "year" && x.price_year;
          const key = yearly ? x.lookup_key_year : x.lookup_key;
          return (
            <Card
              key={x.id}
              className={`flex flex-col p-6 ${current ? "border-2 border-slate-900" : ""}`}
              data-testid={`plan-card-${x.id}`}
            >
              <div className="flex items-baseline justify-between">
                <h3 className="font-head text-xl font-bold">{x.name}</h3>
                {current && <span className="rounded-full bg-slate-900 px-2.5 py-1 text-[10px] font-bold text-white">ATTIVO</span>}
              </div>
              <p className="font-num mt-2 text-3xl font-bold">
                {x.price === 0 ? "Gratis" : eur(yearly ? x.price_year : x.price)}
                {x.price > 0 && <span className="text-xs font-normal text-slate-500">{yearly ? "/anno" : "/mese"} + IVA</span>}
              </p>
              {yearly && (
                <p className="text-xs font-semibold text-emerald-600" data-testid={`plan-save-${x.id}`}>
                  Risparmi {eur(x.price * 12 - x.price_year)} — due mesi in regalo
                </p>
              )}
              <ul className="mt-4 flex-1 space-y-2 text-sm">
                {x.highlights.map((h) => (
                  <li key={h} className="flex items-start gap-2 text-slate-600">
                    <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" /> {h}
                  </li>
                ))}
              </ul>
              {x.id !== "free" && (
                <Button
                  className="mt-5 h-11 w-full"
                  disabled={current || loading === key}
                  data-testid={`choose-plan-${x.id}`}
                  onClick={() => buy(key)}
                >
                  {current ? "Piano attuale" : (
                    <>
                      <Sparkles className="mr-1.5 h-4 w-4" /> {plan.plan === "pro" ? "Passa a" : "Attiva"} {x.name}
                    </>
                  )}
                </Button>
              )}
            </Card>
          );
        })}
      </div>

      <p className="text-xs text-slate-500" data-testid="price-vat-note">
        Prezzi al netto dell'IVA (22% per clienti italiani) · versione beta: nessun addebito reale.
      </p>

      <p className="text-xs text-slate-500">
        Pagamenti gestiti da Stripe in modalità test: usa la carta 4242 4242 4242 4242, una scadenza futura e un CVC
        qualsiasi. L'abbonamento si annulla in autonomia da "Gestisci abbonamento".
      </p>

      <Card className="p-6" data-testid="voucher-card">
        <h3 className="font-head text-lg font-bold">Hai un voucher?</h3>
        <p className="mt-1 text-sm text-slate-500">
          Inserisci il codice ricevuto da Reprehendo: mesi gratis oppure sconto sul prossimo pagamento.
        </p>
        {plan.pending_promo && (
          <p className="mt-3 rounded-md bg-emerald-50 p-3 text-sm text-emerald-800" data-testid="pending-promo">
            Voucher {plan.pending_promo} pronto: lo sconto si applica al prossimo pagamento.
          </p>
        )}
        <div className="mt-3 flex flex-wrap gap-2">
          <Input
            className="h-11 w-full max-w-xs uppercase"
            placeholder="RH-XXXXXX"
            value={voucher}
            onChange={(e) => setVoucher(e.target.value)}
            data-testid="voucher-input"
          />
          <Button
            className="h-11"
            data-testid="redeem-voucher-btn"
            onClick={async () => {
              if (!voucher.trim()) return toast.error("Inserisci il codice");
              try {
                const r = await p("/vouchers/redeem", { code: voucher });
                toast.success(r.message);
                setVoucher("");
                await refreshPlan();
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Codice non valido");
              }
            }}
          >
            <Ticket className="mr-1.5 h-4 w-4" /> Usa il voucher
          </Button>
        </div>
      </Card>
    </div>
  );
}
